/*
 * ============================================================================
 * cs-mega-nav  —  Progressive-Enhancement-Controller (Alsaba / Expanse 9.1.0)
 * ----------------------------------------------------------------------------
 * Vanilla Web-Component, keine Library, defer (Modul). OHNE dieses Skript
 * bleibt die Navigation voll nutzbar:
 *   - Desktop-Panel oeffnet per CSS (:hover / :focus-within),
 *   - alle Links sind erreichbar, der mobile Drawer faellt auf normale Links
 *     zurueck (Toggle ist nur Enhancement).
 *
 * Dieses Skript ergaenzt:
 *   Desktop  - aria-expanded-Sync, Klick-/Touch-Modus, ESC, Outside-Click,
 *              Focus-Leave, Pfeiltasten, optionale Links-Spalten-Umschaltung.
 *   Mobil    - Toggle dispatcht csMegaNav:open/close an das wiederverwendete
 *              <header-drawer> (Scroll-Lock + Fokus-Trap + ESC kommen von dort),
 *              Accordion: Ebenen klappen in place auf/zu (kein Slide).
 * ============================================================================
 */
import { EVENTS } from 'util.events'

const DESKTOP_MQ = '(min-width: 769px)'

class CsMegaNav extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController()
    const signal = this.abortController.signal

    // Markiert, dass JS aktiv ist: deaktiviert die reine :focus-within-Oeffnung
    // (siehe CSS) — ab jetzt steuert dieses Skript den Zustand (ESC etc.).
    this.classList.add('cs-js')

    // cs-lcp2: Knoten-Referenzen in eigener Methode — nach einem Markup-Swap
    // (slim -> full) muessen sie neu gesetzt werden, sonst zeigen sie auf
    // entfernte Knoten. Die Event-Listener bleiben (Delegation auf dem Host).
    this.cacheRefs()
    this._drawerOpen = false

    // Nativen Header-Hamburger (im Sticky-Header) auf den NEUEN Drawer umlenken,
    // damit es nur EINE mobile Navigation gibt. Klappt der native Burger zu,
    // bleibt der cs-Drawer die einzige Quelle. Wird er nicht gefunden, faellt
    // die Komponente auf ihren eigenen "Kategorien"-Button zurueck (CSS-Klasse).
    // Nur die Drawer-haltende Instanz (Section) verdrahtet den Burger — NICHT
    // die Inline-Bar-Instanz im Header (sonst doppeltes Toggeln).
    this.nativeBurger = this.drawer ? document.querySelector('.mobile-nav-trigger') : null
    if (this.nativeBurger) {
      this.classList.add('cs-uses-native-burger')
      this.nativeBurger.setAttribute('aria-controls', 'CsMegaNavDrawer')
      this.nativeBurger.addEventListener('click', this.onNativeBurger.bind(this), { capture: true, signal })
    } else if (this.drawer) {
      // cs-lcp2: Die Fallback-Leiste ist per CSS versteckt (kein Layout-Sprung beim
      // Hydrieren). Nur wenn wirklich kein nativer Burger existiert, blenden wir sie
      // hier ein — sonst gaebe es mobil keinen Weg in den Drawer.
      // Rueckbau: diesen else-Zweig + die beiden CSS-Regeln in cs-mega-nav.liquid entfernen.
      this.classList.add('cs-no-native-burger')
    }

    // ---------- Desktop: ein zentraler Click-/Keydown-/Focus-Handler ----------
    this.addEventListener('click', this.onClick.bind(this), { signal })
    this.addEventListener('keydown', this.onKeydown.bind(this), { signal })
    this.addEventListener('focusout', this.onFocusout.bind(this), { signal })

    // Hinweis: Tabs wechseln NUR per Klick (ruhigere UX, IKEA-Stil) — siehe
    // onTab in der Klick-Delegation. KEIN Umschalten bei Hover/Fokus.

    // Outside-Click schliesst offene Desktop-Panels
    document.addEventListener('click', this.onDocumentClick.bind(this), { signal })

    // ---------- Mobil: Drawer-Schliessen zurueckmelden ----------
    document.addEventListener(EVENTS.headerDrawerClosed, this.onDrawerClosed.bind(this), { signal })

    // ---------- Inline-Instanz im Header: Panel bombensicher positionieren ----------
    // Unabhaengig von CSS-Anker-Problemen im Overlay-Header: per JS die
    // Header-Unterkante messen und das Panel als position:fixed ueber die volle
    // Viewport-Breite direkt darunter legen. Inhalt re-zentriert .page-width.
    if (this.classList.contains('cs-mega-nav--in-header')) {
      this.panels = Array.from(this.querySelectorAll('.cs-mega-nav__panel'))
      const reposition = this.positionPanels.bind(this)
      // cs-tbt: Erst-Positionierung in den naechsten Frame verschieben statt
      // synchron im window-load-Burst (getBoundingClientRect nach den DOM-Writes
      // von clone-footer/ShopVote = forced Layout; Trace 10.07.: home 3,6 /
      // cat 5,6 / pdp 9,9 ms obs). Gefahrlos: mouseenter/focusin/resize
      // re-positionieren VOR jeder Panel-Sichtbarkeit, mobil ist die Leiste
      // display:none. Rueckbau: die if-Zeile durch reposition() ersetzen.
      if (this.isDesktop) requestAnimationFrame(reposition)
      this.addEventListener('mouseenter', reposition, { signal })
      this.addEventListener('focusin', reposition, { signal })
      window.addEventListener('resize', reposition, { signal })
      window.addEventListener('scroll', reposition, { passive: true, signal })
    }

    // cs-navimg: Bilder erst bauen, wenn das Panel gebraucht wird (siehe csFillImages).
    // Desktop = Hover-/Fokus-Absicht auf einer Nav-Saeule; die mobilen Anlaesse haengen
    // direkt an den Bedienhandlern (toggleDrawer/onSegment/onAccordion/onTab).
    if (this.dataset.csNav === 'slim') {
      const intent = this.onFillIntent.bind(this)
      this.addEventListener('pointerover', intent, { signal })
      this.addEventListener('focusin', intent, { signal })
    }
  }

  // ===========================================================================
  //  cs-navimg (18.08.2026): Nav-Bilder aus data-cs-i bauen
  //  Der Server liefert je Platzhalter nur die Bild-URL (siehe cs-mega-nav.tile-img /
  //  cs-mega-nav.acc-thumb). Hier entsteht daraus ein schlichtes <img> — erst fuer den
  //  Container, der gleich sichtbar wird.
  //
  //  Ersetzt den frueheren Section-Rendering-Abruf von cs-mega-nav-full (cs-lcp2 W6a):
  //  der holte 298 KB fuer 262 Bilder auf einmal und baute sie NICHT ein, solange ein
  //  Panel offen war oder der Fokus in der Navigation lag — im Klick-Modus also nie.
  //  Rueckbau: Theme-Einstellung cs_meganav_slim_header ausschalten (Server rendert die
  //  Bilder dann wieder direkt mit). docs/cs-lcp2.md
  // ===========================================================================
  onFillIntent(evt) {
    // Nur die beruehrte Saeule fuellen, nicht die ganze Leiste. Das Panel ist dabei noch
    // visibility:hidden, liegt aber geometrisch im Viewport -> die Bilder laden schon,
    // der Klick danach fuehlt sich sofort an.
    const item = evt.target.closest('.cs-mega-nav__item')
    // pointerover feuert bei jeder Mausbewegung ueber eine Kindgrenze — die Erledigt-Marke
    // spart den wiederholten Teilbaum-Scan. Panes ohne Bilder gelten damit ebenfalls als fertig.
    if (!item || item.dataset.csImgs === 'done') return
    csFillImages(item)
    item.dataset.csImgs = 'done'
  }

  cacheRefs() {
    this.toggle = this.querySelector('[data-cs-toggle]')
    this.drawer = this.querySelector('.cs-mega-nav__drawer')
    this.drill = this.querySelector('[data-cs-drill]')
    this.items = Array.from(this.querySelectorAll('.cs-mega-nav__item'))
    this.triggers = Array.from(this.querySelectorAll('[data-cs-trigger]'))
    this.panels = Array.from(this.querySelectorAll('.cs-mega-nav__panel'))
  }

  positionPanels() {
    const header = document.querySelector('.site-header__element--top') || document.getElementById('SiteHeader')
    if (!header || !this.panels) return
    const bottom = Math.max(0, Math.round(header.getBoundingClientRect().bottom))
    for (const p of this.panels) {
      p.style.position = 'fixed'
      p.style.top = bottom + 'px'
      p.style.left = '0'
      p.style.right = 'auto'
      p.style.width = '100vw'
      p.style.zIndex = '60'
    }
  }

  disconnectedCallback() {
    this.abortController.abort()
  }

  get isDesktop() {
    return window.matchMedia(DESKTOP_MQ).matches
  }

  // ===========================================================================
  //  Zentrale Klick-Delegation
  // ===========================================================================
  onClick(evt) {
    const toggle = evt.target.closest('[data-cs-toggle]')
    if (toggle && this.contains(toggle)) return this.onToggle(evt)

    const close = evt.target.closest('[data-cs-close]')
    if (close) return this.closeDrawer(evt)

    const seg = evt.target.closest('[data-cs-seg]')
    if (seg) return this.onSegment(seg)

    const acc = evt.target.closest('[data-cs-acc]')
    if (acc) return this.onAccordion(acc)

    const tab = evt.target.closest('.cs-mega-nav__tab')
    if (tab) {
      evt.preventDefault()
      return this.onTab(tab)
    }

    const trigger = evt.target.closest('[data-cs-trigger]')
    if (trigger) return this.onTrigger(evt, trigger)
  }

  // Tab im Mega-Panel aktivieren (zeigt zugehoeriges Pane, blendet andere aus).
  onTab(tab) {
    const paneId = tab.getAttribute('data-cs-tab')
    const panel = tab.closest('.cs-mega-nav__megapanel')
    if (!panel) return
    panel.querySelectorAll('.cs-mega-nav__tab').forEach((t) => {
      const on = t === tab
      t.classList.toggle('is-active', on)
      t.setAttribute('aria-selected', on ? 'true' : 'false')
    })
    panel.querySelectorAll('.cs-mega-nav__pane').forEach((p) => {
      p.classList.toggle('is-active', p.id === paneId)
    })
    // cs-navimg: erst is-active setzen, dann fuellen — in einem display:none-Pane wuerde
    // ein loading="lazy"-Bild nie geladen.
    csFillImages(panel.querySelector('.cs-mega-nav__pane.is-active'))
  }


  // ===========================================================================
  //  DESKTOP
  // ===========================================================================
  onTrigger(evt, trigger) {
    // Im Hover-Modus uebernimmt CSS das Oeffnen; Klick toggelt zusaetzlich
    // (fuer Touch/Tastatur). Im Klick-Modus ist der Klick die einzige Quelle.
    evt.preventDefault()
    const item = trigger.closest('.cs-mega-nav__item')
    // cs-navimg: Sicherheitsnetz. Normal hat pointerover/focusin schon gefuellt; bei Touch
    // am Desktop oder exotischer Eingabe kann der Klick der erste Anlass sein.
    csFillImages(item)
    const isOpen = item.classList.contains('is-open')
    this.closeAllPanels(item)
    if (isOpen) {
      this.setPanel(item, false)
    } else {
      this.setPanel(item, true)
    }
  }

  setPanel(item, open) {
    const trigger = item.querySelector('[data-cs-trigger]')
    item.classList.toggle('is-open', open)
    if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false')
  }

  closeAllPanels(except) {
    this.items.forEach((item) => {
      if (item !== except && item.classList.contains('is-open')) this.setPanel(item, false)
    })
  }

  onKeydown(evt) {
    // Enter/Space werden NICHT hier behandelt — der native Button-Klick
    // (onTrigger) uebernimmt das Umschalten, sonst doppeltes Toggeln.
    if (!this.isDesktop) return

    if (evt.key === 'Escape') {
      const item = evt.target.closest('.cs-mega-nav__item.is-open')
      if (item) {
        this.setPanel(item, false)
        item.querySelector('[data-cs-trigger]')?.focus()
        evt.stopPropagation()
      }
      return
    }

    const trigger = evt.target.closest('[data-cs-trigger]')
    if (!trigger) return
    const item = trigger.closest('.cs-mega-nav__item')

    if (evt.key === 'ArrowDown') {
      // Panel oeffnen und in die erste Panel-Aktion springen.
      evt.preventDefault()
      this.closeAllPanels(item)
      this.setPanel(item, true)
      item.querySelector('.cs-mega-nav__panel a, .cs-mega-nav__panel button')?.focus()
    } else if (evt.key === 'ArrowRight' || evt.key === 'ArrowLeft') {
      // Roving zwischen den Top-Triggern.
      evt.preventDefault()
      const i = this.triggers.indexOf(trigger)
      const next = evt.key === 'ArrowRight' ? i + 1 : i - 1
      this.triggers[(next + this.triggers.length) % this.triggers.length]?.focus()
    }
  }

  onFocusout(evt) {
    // Wenn der Fokus das Item komplett verlaesst, Panel schliessen.
    if (!this.isDesktop) return
    const item = evt.target.closest('.cs-mega-nav__item')
    if (!item || !item.classList.contains('is-open')) return
    if (!item.contains(evt.relatedTarget)) this.setPanel(item, false)
  }

  onDocumentClick(evt) {
    if (!this.isDesktop) return
    if (this.contains(evt.target)) return
    this.closeAllPanels(null)
  }

  // ===========================================================================
  //  MOBIL — Drawer + Drill-Down
  // ===========================================================================
  onToggle(evt) {
    // Eigener "Kategorien"-Button (Fallback, falls kein nativer Burger da ist).
    evt.preventDefault()
    this.toggleDrawer(this.toggle)
  }

  onNativeBurger(evt) {
    // Nativer Header-Hamburger: nativen Handler stoppen und cs-Drawer steuern.
    evt.preventDefault()
    evt.stopImmediatePropagation()
    this.toggleDrawer(this.nativeBurger)
  }

  toggleDrawer(trigger) {
    if (this._drawerOpen) {
      this.closeDrawer()
      return
    }
    this.resetDrill()
    // Auf dem Trigger dispatchen, damit der Fokus beim Schliessen dorthin zurueckkehrt.
    trigger.dispatchEvent(new CustomEvent('csMegaNav:open', { bubbles: true }))
    trigger.setAttribute('aria-expanded', 'true')
    trigger.classList.add('is-active')
    this._drawerOpen = true
    // cs-navimg: Bilder des sichtbaren Segments bauen (resetDrill hat auf Segment 1 gestellt).
    csFillImages(this.drill?.querySelector('.cs-mega-nav__seg-panel.is-active') || this.drill)
  }

  closeDrawer(evt) {
    if (evt) evt.preventDefault()
    // WICHTIG: auf document dispatchen — header.drawer ignoriert Klicks, deren
    // Ziel innerhalb des Drawers liegt (Close-Button waere sonst wirkungslos).
    document.dispatchEvent(new CustomEvent('csMegaNav:close'))
  }

  onDrawerClosed(evt) {
    // Nur die Drawer-Instanz reagiert, und nur auf IHREN Drawer.
    if (!this.drawer || evt.target !== this.drawer) return
    this._drawerOpen = false
    for (const t of [this.toggle, this.nativeBurger]) {
      if (!t) continue
      t.setAttribute('aria-expanded', 'false')
      t.classList.remove('is-active')
    }
    this.resetDrill()
  }

  onAccordion(button) {
    // Klappt die zugehoerige Ebene IN PLACE auf/zu (grid-rows via CSS).
    const collapse = this.drill.querySelector(`#${CSS.escape(button.getAttribute('aria-controls'))}`)
    if (!collapse) return
    const willOpen = button.getAttribute('aria-expanded') !== 'true'
    button.setAttribute('aria-expanded', willOpen ? 'true' : 'false')
    collapse.classList.toggle('is-active', willOpen)
    // cs-navimg: eingeklappte Ebenen sind per overflow:hidden geclippt -> erst aufklappen,
    // dann fuellen, sonst haelt der Browser die lazy-Bilder zurueck.
    if (willOpen) csFillImages(collapse)
  }

  // Mobiler "Produkte | Raeume"-Umschalter: aktives Segment + Panel setzen.
  onSegment(btn) {
    const scope = this.drill
    if (!scope) return
    const id = btn.getAttribute('data-cs-seg')
    scope.querySelectorAll('.cs-mega-nav__seg-btn').forEach((b) => {
      const on = b === btn
      b.classList.toggle('is-active', on)
      b.setAttribute('aria-selected', on ? 'true' : 'false')
    })
    scope.querySelectorAll('.cs-mega-nav__seg-panel').forEach((p) => {
      p.classList.toggle('is-active', p.id === id)
    })
    csFillImages(scope.querySelector('.cs-mega-nav__seg-panel.is-active'))
    scope.scrollTop = 0
  }

  resetDrill() {
    // Beim (Wieder-)Oeffnen des Drawers alle Ebenen einklappen.
    if (!this.drill) return
    this.drill.querySelectorAll('.cs-mega-nav__acc-collapse.is-active').forEach((c) => c.classList.remove('is-active'))
    this.drill.querySelectorAll('[data-cs-acc][aria-expanded="true"]').forEach((b) => b.setAttribute('aria-expanded', 'false'))
    // Umschalter (falls vorhanden) auf den ersten Bereich zuruecksetzen.
    const segBtns = this.drill.querySelectorAll('.cs-mega-nav__seg-btn')
    if (segBtns.length) {
      segBtns.forEach((b, i) => {
        const on = i === 0
        b.classList.toggle('is-active', on)
        b.setAttribute('aria-selected', on ? 'true' : 'false')
      })
      this.drill.querySelectorAll('.cs-mega-nav__seg-panel').forEach((p, i) => p.classList.toggle('is-active', i === 0))
    }
    this.drill.scrollTop = 0
  }
}

// cs-navimg: aus data-cs-i ein <img> bauen. Zwei Groessen-Profile, unterschieden an der
// Klasse des Platzhalters. Der Server liefert die nackte CDN-URL (mit ?v=<hash>), die
// Breiten haengen wir hier an — das spart ~16 B je Bild im HTML.
const CS_IMG_PRESETS = {
  tile: { widths: [300, 600], sizes: '(min-width: 769px) 220px, 45vw' },
  thumb: { widths: [44, 88, 132], sizes: '44px' },
}

function csFillImages(root) {
  if (!root) return
  // data-cs-i wird beim Bauen entfernt -> der Selektor ueberspringt Erledigtes von selbst,
  // wiederholte Aufrufe (pointerover feuert oft) kosten dann nur die Suche.
  const spans = root.querySelectorAll('[data-cs-i]')
  for (const span of spans) {
    const url = span.dataset.csI
    delete span.dataset.csI
    if (!url) continue
    const preset = span.classList.contains('cs-mega-nav__acc-thumb') ? CS_IMG_PRESETS.thumb : CS_IMG_PRESETS.tile
    const img = document.createElement('img')
    const at = (w) => `${url}&width=${w}`
    img.srcset = preset.widths.map((w) => `${at(w)} ${w}w`).join(', ')
    img.src = at(preset.widths[preset.widths.length - 1])
    img.sizes = preset.sizes
    // lazy trotz sichtbarem Container: in langen Listen (mobile Kacheln, Accordion-Ebenen)
    // laedt der Browser so nur, was wirklich im Blickfeld liegt.
    img.loading = 'lazy'
    img.decoding = 'async'
    // Dekorativ: der Textlabel steht unmittelbar daneben und benennt den Link bereits.
    img.alt = ''
    span.appendChild(img)
  }
}

customElements.define('cs-mega-nav', CsMegaNav)
